import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { callMonitor } from '../../services/callMonitor.service';

const CallManagerContext = createContext();

export const useCallManager = () => {
  const context = useContext(CallManagerContext);
  if (!context) {
    throw new Error('useCallManager must be used within a CallManagerProvider');
  }
  return context;
};

export const CallManagerContextProvider = ({ children }) => {
  const [activeCalls, setActiveCalls] = useState([]);
  const [completedCalls, setCompletedCalls] = useState([]);
  const [flaggedCalls, setFlaggedCalls] = useState([]);
  const animationFrameRef = useRef(null);
  const lastUpdateTimeRef = useRef(Date.now());

  const startCall = (queueItem) => {
    const activeCall = {
      ...queueItem,
      callId: `CALL-${Math.floor(Math.random() * 10000)}`,
      startTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      duration: '0:00',
      status: 'Queued',
      callStatus: 'Human',
      startTimestamp: Date.now(),
    };

    setActiveCalls(prev => [...prev, activeCall]);
    
    if (!animationFrameRef.current) {
      lastUpdateTimeRef.current = Date.now();
      animationFrameRef.current = requestAnimationFrame(updateDurations);
    }
    
    return activeCall;
  };

  const updateCallStatus = (orderLineId, updates) => {
    setActiveCalls(prev => 
      prev.map(call => {
        if (call.orderLineId === String(orderLineId)) {
          return {
            ...call,
            ...updates,
            duration: updates.duration ? formatDuration(updates.duration) : call.duration,
          };
        }
        return call;
      })
    );
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const updateDurations = () => {
    const now = Date.now();
    const elapsed = Math.floor((now - lastUpdateTimeRef.current) / 1000);
    
    if (elapsed >= 1) {
      lastUpdateTimeRef.current = now;
      
      setActiveCalls(prev => 
        prev.map(call => {
          const totalElapsed = Math.floor((now - call.startTimestamp) / 1000);
          return {
            ...call,
            duration: formatDuration(totalElapsed),
          };
        })
      );
    }
    
    animationFrameRef.current = requestAnimationFrame(updateDurations);
  };

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const value = {
    activeCalls,
    completedCalls,
    flaggedCalls,
    startCall,
    updateCallStatus,
  };

  return (
    <CallManagerContext.Provider value={value}>
      {children}
    </CallManagerContext.Provider>
  );
}; 